import {
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  Bot,
  ClipboardCheck,
  DollarSign,
  Download,
  ExternalLink,
  FileText,
  Home,
  Inbox,
  LogOut,
  Loader2,
  Search,
  ShieldCheck,
  ShoppingBag,
  Users,
  UserSearch,
  Wrench,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ThemeToggle } from "@/components/app/ThemeToggle";
import { cn } from "@/lib/utils";
import { api } from "@/lib/api";

const navGroups = [
  {
    label: "Work",
    items: [
      { to: "/admin", label: "Ops home", icon: Home, match: ["/admin"] },
      { to: "/admin/queue", label: "Takedown inbox", icon: Inbox },
      {
        to: "/admin/scrapes?status=submitted",
        label: "Scrape queue",
        icon: ClipboardCheck,
        match: ["/admin/scrapes", "/admin/scrapes/"],
      },
      {
        to: "/admin/agent-runs?status=needs_human",
        label: "Agent handoffs",
        icon: Bot,
        match: ["/admin/agent-runs", "/admin/agent-runs/"],
      },
      { to: "/admin/audits", label: "Audits", icon: FileText },
    ],
  },
  {
    label: "Accounts",
    items: [
      { to: "/admin/customers", label: "Customers", icon: Users },
      { to: "/admin/prospects", label: "Prospects", icon: UserSearch },
    ],
  },
  {
    label: "System",
    items: [
      { to: "/admin/unit-economics", label: "Unit economics", icon: DollarSign },
      { to: "/admin/retail-accounts", label: "Retail auth", icon: ShoppingBag },
      { to: "/admin/manual-scrapers", label: "Scrapers", icon: ShieldCheck },
      { to: "/manual-scrape", label: "Worker portal", icon: ExternalLink },
    ],
  },
];

type RetailContextStatus = {
  needsLogin: boolean;
  activeContextCount: number;
  pendingManualFallbackCount: number;
  oldestPendingManualFallback: {
    id: string;
    asin: string;
    createdAt: string;
  } | null;
};

type ProductSearchResult = {
  id: string;
  asin: string;
  productTitle: string | null;
  monitoringEnabled: boolean;
  brand: { id: string; name: string };
  customer: { id: string; organizationName: string; email: string };
  reviewCount: number;
  flaggedCount: number;
};

type ProductSearchResponse = {
  products: ProductSearchResult[];
};

function AdminProductJumpSearch() {
  const navigate = useNavigate();
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<ProductSearchResult[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const trimmedQuery = query.trim();

  useEffect(() => {
    function onKeyDown(event: globalThis.KeyboardEvent) {
      if (event.key !== "/" || event.metaKey || event.ctrlKey || event.altKey) {
        return;
      }
      const target = event.target;
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        (target instanceof HTMLElement && target.isContentEditable)
      ) {
        return;
      }
      event.preventDefault();
      inputRef.current?.focus();
    }

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  useEffect(() => {
    function onPointerDown(event: PointerEvent) {
      if (!wrapperRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    }

    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, []);

  useEffect(() => {
    if (trimmedQuery.length < 2) {
      setResults([]);
      setLoading(false);
      setError(null);
      setActiveIndex(0);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    const handle = window.setTimeout(() => {
      api<ProductSearchResponse>(
        `/admin/products/search?q=${encodeURIComponent(trimmedQuery)}`
      ).then((res) => {
        if (cancelled) return;
        setLoading(false);
        if (!res.ok || !res.data) {
          setResults([]);
          setError(res.error ?? "Search failed");
          return;
        }
        setResults(res.data.products);
        setActiveIndex(0);
        setOpen(true);
      });
    }, 180);

    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [trimmedQuery]);

  function clearSearch() {
    setQuery("");
    setResults([]);
    setOpen(false);
    setError(null);
    setActiveIndex(0);
  }

  function openReviews(product: ProductSearchResult) {
    clearSearch();
    navigate(`/admin/products/${product.id}/reviews`);
  }

  function onInputKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Escape") {
      setOpen(false);
      return;
    }

    if (!open && ["ArrowDown", "ArrowUp", "Enter"].includes(event.key)) {
      setOpen(true);
    }

    if (event.key === "ArrowDown" && results.length > 0) {
      event.preventDefault();
      setActiveIndex((current) => Math.min(current + 1, results.length - 1));
    } else if (event.key === "ArrowUp" && results.length > 0) {
      event.preventDefault();
      setActiveIndex((current) => Math.max(current - 1, 0));
    } else if (event.key === "Enter" && results[activeIndex]) {
      event.preventDefault();
      openReviews(results[activeIndex]);
    }
  }

  const showResults = open && trimmedQuery.length >= 2;

  return (
    <div ref={wrapperRef} className="relative min-w-0 flex-1 lg:max-w-2xl">
      <label className="flex h-9 items-center gap-2 rounded-lg border border-input bg-background px-2.5 text-sm shadow-sm focus-within:border-slate-400">
        <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
        <input
          ref={inputRef}
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
          }}
          onFocus={() => {
            if (trimmedQuery.length >= 2) setOpen(true);
          }}
          onKeyDown={onInputKeyDown}
          placeholder="Search ASIN or product"
          className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          aria-label="Search ASIN or product"
          autoComplete="off"
        />
        <span className="hidden rounded border border-border px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground sm:inline">
          /
        </span>
      </label>

      {showResults && (
        <div className="absolute left-0 right-0 top-full z-50 mt-2 max-h-[min(70vh,28rem)] overflow-y-auto rounded-xl border border-border bg-popover p-1 text-popover-foreground shadow-xl">
          {loading && (
            <div className="flex items-center gap-2 px-3 py-3 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Searching...
            </div>
          )}
          {!loading && error && (
            <div className="px-3 py-3 text-sm text-red-600">{error}</div>
          )}
          {!loading && !error && results.length === 0 && (
            <div className="px-3 py-3 text-sm text-muted-foreground">
              No matching products.
            </div>
          )}
          {!loading &&
            !error &&
            results.map((product, index) => (
              <div
                key={product.id}
                onMouseEnter={() => setActiveIndex(index)}
                className={cn(
                  "flex items-stretch gap-1 rounded-lg",
                  index === activeIndex && "bg-muted"
                )}
              >
                <button
                  type="button"
                  onClick={() => openReviews(product)}
                  className="min-w-0 flex-1 px-3 py-2 text-left"
                >
                  <div className="flex min-w-0 items-center gap-2">
                    <span className="font-mono text-sm font-semibold text-foreground">
                      {product.asin}
                    </span>
                    <span
                      className={cn(
                        "rounded-full px-2 py-0.5 text-[11px] font-medium",
                        product.monitoringEnabled
                          ? "bg-emerald-50 text-emerald-700"
                          : "bg-muted text-muted-foreground"
                      )}
                    >
                      {product.monitoringEnabled ? "Monitored" : "Paused"}
                    </span>
                    {product.flaggedCount > 0 && (
                      <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700">
                        {product.flaggedCount} flagged
                      </span>
                    )}
                  </div>
                  <div className="mt-0.5 truncate text-xs text-muted-foreground">
                    {product.productTitle || product.brand.name}
                  </div>
                  <div className="mt-0.5 truncate text-[11px] text-muted-foreground">
                    {product.customer.organizationName} / {product.brand.name} ·{" "}
                    {product.reviewCount} reviews
                  </div>
                </button>
                <Link
                  to={`/admin/products/${product.id}`}
                  onClick={clearSearch}
                  className="flex w-10 shrink-0 items-center justify-center rounded-r-lg text-muted-foreground hover:bg-background hover:text-foreground"
                  title="Open product details"
                  aria-label={`Open ${product.asin} details`}
                >
                  <ExternalLink className="h-4 w-4" />
                </Link>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}

export function AdminLayout({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [retailStatus, setRetailStatus] =
    useState<RetailContextStatus | null>(null);

  useEffect(() => {
    let cancelled = false;
    api<RetailContextStatus>("/admin/retail-context-status").then((res) => {
      if (cancelled) return;
      if (res.ok && res.data) setRetailStatus(res.data);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  async function onSignOut() {
    await api("/admin/logout", { method: "POST" });
    navigate("/admin/login", { replace: true });
  }

  function isActive(item: { to: string; match?: string[] }) {
    const pathname = location.pathname;
    if (item.match?.some((path) => pathname === path || pathname.startsWith(path))) {
      if (item.to === "/admin") return pathname === "/admin";
      return true;
    }
    const itemPath = item.to.split("?")[0];
    return pathname === itemPath || pathname.startsWith(`${itemPath}/`);
  }

  const nav = (
    <nav className="space-y-6">
      {navGroups.map((group) => (
        <div key={group.label}>
          <div className="px-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            {group.label}
          </div>
          <div className="mt-1 space-y-0.5">
            {group.items.map((item) => {
              const Icon = item.icon;
              const active = isActive(item);
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={cn(
                    "flex items-center gap-2 rounded-lg px-2 py-2 text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground",
                    active && "bg-muted text-foreground"
                  )}
                >
                  <Icon className="h-4 w-4" />
                  <span className="min-w-0 truncate">{item.label}</span>
                </Link>
              );
            })}
          </div>
        </div>
      ))}
    </nav>
  );

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="flex min-h-screen">
        <aside className="hidden w-64 shrink-0 border-r border-border bg-card/80 px-4 py-4 lg:block">
          <Link
            to="/admin"
            className="flex items-center gap-2 px-2 text-base font-semibold tracking-tight"
          >
            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-slate-900 text-xs font-semibold text-white dark:bg-slate-100 dark:text-slate-900">
              R
            </span>
            <span>Reputely admin</span>
          </Link>
          <div className="mt-8">{nav}</div>
        </aside>

        <div className="min-w-0 flex-1">
          <header className="sticky top-0 z-30 border-b border-border bg-background/95 backdrop-blur">
            <div className="flex h-14 items-center justify-between gap-3 px-4 sm:px-6">
              <Link
                to="/admin"
                className="flex items-center gap-2 font-semibold tracking-tight lg:hidden"
              >
                <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-slate-900 text-xs font-semibold text-white dark:bg-slate-100 dark:text-slate-900">
                  R
                </span>
                <span>Admin</span>
              </Link>
              <div className="hidden items-center gap-2 text-sm text-muted-foreground lg:flex">
                <Wrench className="h-4 w-4" />
                Operator workspace
              </div>
              <AdminProductJumpSearch />
              <div className="flex shrink-0 items-center gap-2">
                <a
                  href="/api/admin/training-export.jsonl"
                  className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground"
                  title="Export training data"
                  aria-label="Export training data"
                >
                  <Download className="h-4 w-4" />
                </a>
                <ThemeToggle />
                <Button variant="ghost" size="sm" onClick={onSignOut}>
                  <LogOut className="h-4 w-4" />
                  <span className="hidden sm:inline">Sign out</span>
                </Button>
              </div>
            </div>
            <div className="overflow-x-auto border-t border-border px-4 py-2 [scrollbar-width:none] sm:px-6 lg:hidden [&::-webkit-scrollbar]:hidden">
              <div className="flex w-max gap-1">
                {navGroups.flatMap((group) => group.items).map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item);
                  return (
                    <Link
                      key={item.to}
                      to={item.to}
                      className={cn(
                        "inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm text-muted-foreground hover:bg-muted hover:text-foreground",
                        active && "bg-muted text-foreground"
                      )}
                    >
                      <Icon className="h-3.5 w-3.5" />
                      {item.label}
                    </Link>
                  );
                })}
              </div>
            </div>
          </header>

          {retailStatus?.needsLogin && (
            <div className="border-b border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
              <div className="flex flex-wrap items-center gap-3 px-4 py-3 text-sm sm:px-6">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                <div className="min-w-0 flex-1">
                  <span className="font-medium">Amazon re-login required.</span>{" "}
                  Authenticated review scraping is unavailable.
                  {retailStatus.oldestPendingManualFallback && (
                    <>
                      {" "}
                      Oldest pending ASIN:{" "}
                      <span className="font-mono">
                        {retailStatus.oldestPendingManualFallback.asin}
                      </span>
                      .
                    </>
                  )}
                </div>
                <Link
                  to="/admin/retail-accounts"
                  className="shrink-0 rounded-lg bg-amber-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-800"
                >
                  Re-login
                </Link>
              </div>
            </div>
          )}

          <main className="w-full px-4 py-6 sm:px-6 lg:px-8">{children}</main>
        </div>
      </div>
    </div>
  );
}
